import React, { useState } from 'react';
import { AlertTriangle, Edit, GripVertical, Plus, Shield, Trash2 } from 'lucide-react';
import {
    closestCenter,
    DndContext,
    DragEndEvent,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import {
    Dialog,
    DialogBody,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from '@/components/ui/dialog';
import {
    AlertDialog,
    AlertDialogAction,
    AlertDialogBody,
    AlertDialogCancel,
    AlertDialogContent,
    AlertDialogDescription,
    AlertDialogFooter,
    AlertDialogHeader,
    AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { usePermissions } from '@/hooks/usePermissions';
import {
    CreateLeadStatusRequest,
    LeadStatus,
    UpdateLeadStatusRequest,
    useCreateLeadStatus,
    useDeleteLeadStatus,
    useLeadStatuses,
    useReorderLeadStatuses,
    useSeedDefaultLeadStatuses,
    useToggleLeadStatus,
    useUpdateLeadStatus,
} from '@/api/lead-statuses/use-lead-statuses';
import { LeadStatusForm } from './LeadStatusForm';
import { ModulePermission, PermissionAction } from '@/types/permissions.ts';

interface LeadStatusManagerProps {
    className?: string;
}

export const LeadStatusManager: React.FC<LeadStatusManagerProps> = ({ className }) => {
    const { hasPermission } = usePermissions();
    const canManageStatuses =
        hasPermission(ModulePermission.SYSTEM_SETTINGS, PermissionAction.EDIT) || false;

    const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
    const [editingStatus, setEditingStatus] = useState<LeadStatus | null>(null);
    const [deletingStatus, setDeletingStatus] = useState<LeadStatus | null>(null);
    const [replacementStatusId, setReplacementStatusId] = useState<string>('');

    // API hooks
    const { data: statusesData, isLoading } = useLeadStatuses();
    const createStatusMutation = useCreateLeadStatus();
    const updateStatusMutation = useUpdateLeadStatus();
    const deleteStatusMutation = useDeleteLeadStatus();
    const toggleStatusMutation = useToggleLeadStatus();
    const reorderStatusesMutation = useReorderLeadStatuses();
    const seedDefaultsMutation = useSeedDefaultLeadStatuses();

    const statuses = statusesData?.data || [];

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    const handleCreateStatus = async (data: CreateLeadStatusRequest) => {
        try {
            await createStatusMutation.mutateAsync(data);
            setIsCreateDialogOpen(false);
        } catch (error) {
            // Error handling is done in the mutation hook
        }
    };

    const handleUpdateStatus = async (id: string, data: UpdateLeadStatusRequest) => {
        try {
            await updateStatusMutation.mutateAsync({ id, data });
            setEditingStatus(null);
        } catch (error) {
            // Error handling is done in the mutation hook
        }
    };

    const handleDeleteStatus = async (status: LeadStatus) => {
        try {
            if (status.usage_count && status.usage_count > 0) {
                if (!replacementStatusId) {
                    return; // Should not happen due to disabled button
                }
                await deleteStatusMutation.mutateAsync({
                    id: status.id,
                    replacementStatusId,
                });
            } else {
                // No replacement needed for unused statuses
                await deleteStatusMutation.mutateAsync({
                    id: status.id,
                });
            }
            setDeletingStatus(null);
            setReplacementStatusId('');
        } catch (error) {
            // Error handling is done in the mutation hook
        }
    };

    const handleToggleStatus = async (id: string) => {
        try {
            await toggleStatusMutation.mutateAsync(id);
        } catch (error) {
            // Error handling is done in the mutation hook
        }
    };

    const handleReorderStatuses = async (newOrder: Array<{ id: string; sort_order: number }>) => {
        try {
            await reorderStatusesMutation.mutateAsync({ order: newOrder });
        } catch (error) {
            // Error handling is done in the mutation hook
        }
    };

    const handleSeedDefaults = async () => {
        try {
            await seedDefaultsMutation.mutateAsync();
        } catch (error) {
            // Error handling is done in the mutation hook
        }
    };

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;

        if (active.id !== over?.id) {
            const oldIndex = statuses.findIndex(status => status.id === active.id);
            const newIndex = statuses.findIndex(status => status.id === over?.id);

            if (oldIndex !== -1 && newIndex !== -1) {
                const newOrder = arrayMove(statuses, oldIndex, newIndex).map((item, index) => ({
                    id: item.id,
                    sort_order: index + 1,
                }));

                handleReorderStatuses(newOrder);
            }
        }
    };

    if (!canManageStatuses) {
        return (
            <Card className={className}>
                <CardHeader>
                    <CardTitle>Lead Status Management</CardTitle>
                    <CardDescription>
                        Manage lead statuses and their visual appearance
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    <div className="text-center py-8 text-muted-foreground">
                        <Shield className="h-12 w-12 mx-auto mb-4 opacity-50" />
                        <p>You don't have permission to manage lead statuses.</p>
                        <p className="text-sm">Contact your administrator for access.</p>
                    </div>
                </CardContent>
            </Card>
        );
    }

    return (
        <div className={className}>
            {/* Header */}
            <Card>
                <CardHeader>
                    <div className="flex items-center justify-between">
                        <div>
                            <CardTitle>Lead Status Management</CardTitle>
                            <CardDescription>
                                Manage lead statuses, colors, and system protection
                            </CardDescription>
                        </div>
                        <div className="flex gap-2">
                            <Button
                                variant="outline"
                                onClick={handleSeedDefaults}
                                disabled={seedDefaultsMutation.isPending}
                            >
                                {seedDefaultsMutation.isPending ? 'Seeding...' : 'Seed Defaults'}
                            </Button>
                            <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
                                <DialogTrigger asChild>
                                    <Button>
                                        <Plus className="h-4 w-4 mr-2" />
                                        Add Status
                                    </Button>
                                </DialogTrigger>
                                <DialogContent>
                                    <DialogHeader>
                                        <DialogTitle>Create New Lead Status</DialogTitle>
                                        <DialogDescription>
                                            Add a new custom lead status with color and label
                                        </DialogDescription>
                                    </DialogHeader>
                                    <DialogBody>
                                        <LeadStatusForm
                                            onSubmit={handleCreateStatus}
                                            onCancel={() => setIsCreateDialogOpen(false)}
                                            isLoading={createStatusMutation.isPending}
                                        />
                                    </DialogBody>
                                </DialogContent>
                            </Dialog>
                        </div>
                    </div>
                </CardHeader>
            </Card>

            {/* Status List */}
            <Card className="mt-6">
                <CardHeader>
                    <CardTitle>Statuses ({statuses.length})</CardTitle>
                    <CardDescription>
                        System statuses cannot be deleted or renamed, but colors can be changed
                    </CardDescription>
                </CardHeader>
                <CardContent>
                    {isLoading ? (
                        <div className="text-center py-8">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                            <p className="mt-2 text-muted-foreground">Loading statuses...</p>
                        </div>
                    ) : statuses.length === 0 ? (
                        <div className="text-center py-8 text-muted-foreground">
                            <p>No lead statuses found.</p>
                            <p className="text-sm">
                                Click "Seed Defaults" to create initial statuses.
                            </p>
                        </div>
                    ) : (
                        <DndContext
                            sensors={sensors}
                            collisionDetection={closestCenter}
                            onDragEnd={onDragEnd}
                        >
                            <SortableContext
                                items={statuses.map(status => status.id)}
                                strategy={verticalListSortingStrategy}
                            >
                                <div className="space-y-4">
                                    {statuses.map((status, index) => (
                                        <SortableStatusItem
                                            key={status.id}
                                            status={status}
                                            index={index}
                                            onEdit={() => setEditingStatus(status)}
                                            onToggle={() => handleToggleStatus(status.id)}
                                            onDelete={() => setDeletingStatus(status)}
                                            setReplacementStatusId={setReplacementStatusId}
                                        />
                                    ))}
                                </div>
                            </SortableContext>
                        </DndContext>
                    )}
                </CardContent>
            </Card>

            {/* Edit Dialog */}
            {editingStatus && (
                <Dialog open={!!editingStatus} onOpenChange={() => setEditingStatus(null)}>
                    <DialogContent>
                        <DialogHeader>
                            <DialogTitle>Edit Lead Status</DialogTitle>
                            <DialogDescription>
                                {editingStatus.is_system
                                    ? 'System statuses can only have colors and order changed'
                                    : 'Update the status label, color, and other properties'}
                            </DialogDescription>
                        </DialogHeader>
                        <DialogBody>
                            <LeadStatusForm
                                initialData={editingStatus}
                                onSubmit={data => handleUpdateStatus(editingStatus.id, data)}
                                onCancel={() => setEditingStatus(null)}
                                isLoading={updateStatusMutation.isPending}
                                isSystem={editingStatus.is_system}
                            />
                        </DialogBody>
                    </DialogContent>
                </Dialog>
            )}

            {/* Delete Confirmation Dialog */}
            <AlertDialog
                open={!!deletingStatus}
                onOpenChange={() => {
                    setDeletingStatus(null);
                    setReplacementStatusId('');
                }}
            >
                <AlertDialogContent className="max-w-lg">
                    <AlertDialogHeader>
                        <AlertDialogTitle className="flex items-center gap-2">
                            <Trash2 className="h-5 w-5 text-destructive" />
                            {deletingStatus?.usage_count && deletingStatus.usage_count > 0
                                ? 'Delete Status (Replacement Required)'
                                : 'Delete Lead Status'}
                        </AlertDialogTitle>
                        <AlertDialogDescription className="text-base">
                            {deletingStatus?.usage_count && deletingStatus.usage_count > 0
                                ? `This status is currently in use by ${deletingStatus.usage_count} leads/customers. You must select a replacement status before deletion.`
                                : 'Are you sure you want to delete this status? This action cannot be undone.'}
                        </AlertDialogDescription>
                    </AlertDialogHeader>

                    {/* Status being deleted */}
                    <AlertDialogBody>
                        <div className="flex items-center gap-3 p-4 bg-muted rounded-lg border">
                            <div
                                className="w-5 h-5 rounded-full border-2 border-border"
                                style={{ backgroundColor: deletingStatus?.color }}
                            />
                            <div>
                                <div className="font-semibold text-foreground">
                                    {deletingStatus?.label}
                                </div>
                                <div className="text-sm text-muted-foreground">
                                    {deletingStatus?.name}
                                </div>
                            </div>
                        </div>
                    </AlertDialogBody>

                    {deletingStatus?.usage_count !== undefined &&
                        deletingStatus.usage_count > 0 && (
                            <AlertDialogBody>
                                <div className="space-y-4 py-4">
                                    <div className="flex items-start gap-3 p-4 bg-red-50 border border-red-200 rounded-lg">
                                        <AlertTriangle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
                                        <div className="text-sm">
                                            <p className="font-medium text-red-800 mb-1">
                                                ⚠️ Replacement Status Required
                                            </p>
                                            <p className="text-red-700">
                                                This status is currently used by{' '}
                                                <span className="font-semibold">
                                                    {deletingStatus.usage_count}
                                                </span>{' '}
                                                leads/customers. You <strong>must</strong> select a
                                                replacement status to reassign them before deletion.
                                            </p>
                                        </div>
                                    </div>

                                    <div className="space-y-3">
                                        <label className="block text-sm font-medium text-foreground">
                                            Select replacement status:{' '}
                                            <span className="text-red-600">*</span>
                                        </label>
                                        <div className="grid gap-2">
                                            {statuses
                                                .filter(
                                                    s => s.id !== deletingStatus?.id && s.is_active
                                                )
                                                .map(status => (
                                                    <label
                                                        key={status.id}
                                                        className="flex items-center gap-3 p-3 border rounded-lg hover:bg-accent cursor-pointer transition-colors"
                                                    >
                                                        <input
                                                            type="radio"
                                                            name="replacement_status"
                                                            value={status.id}
                                                            checked={
                                                                replacementStatusId === status.id
                                                            }
                                                            onChange={e =>
                                                                setReplacementStatusId(
                                                                    e.target.value
                                                                )
                                                            }
                                                            className="h-4 w-4 text-primary border-gray-300 focus:ring-primary"
                                                        />
                                                        <div className="flex items-center gap-2 flex-1">
                                                            <div
                                                                className="w-4 h-4 rounded-full border-2 border-border"
                                                                style={{
                                                                    backgroundColor: status.color,
                                                                }}
                                                            />
                                                            <span className="font-medium">
                                                                {status.label}
                                                            </span>
                                                        </div>
                                                    </label>
                                                ))}
                                        </div>

                                        {/* Show warning if no replacement selected */}
                                        {!replacementStatusId && (
                                            <div className="text-center py-3">
                                                <div className="text-red-600 text-sm font-medium">
                                                    Please select a replacement status to continue
                                                </div>
                                            </div>
                                        )}

                                        {/* Show confirmation message when replacement is selected */}
                                        {replacementStatusId && (
                                            <div className="text-center py-3">
                                                <div className="text-green-600 text-sm font-medium">
                                                    ✓ Replacement selected -{' '}
                                                    {
                                                        statuses.find(
                                                            s => s.id === replacementStatusId
                                                        )?.label
                                                    }
                                                </div>
                                                <div className="text-xs text-muted-foreground mt-1">
                                                    All leads using "{deletingStatus?.label}" will
                                                    be moved to "
                                                    {
                                                        statuses.find(
                                                            s => s.id === replacementStatusId
                                                        )?.label
                                                    }
                                                    "
                                                </div>
                                            </div>
                                        )}

                                        {statuses.filter(
                                            s => s.id !== deletingStatus?.id && s.is_active
                                        ).length === 0 && (
                                            <div className="text-center py-6">
                                                <div className="text-red-600 mb-2">
                                                    <AlertTriangle className="h-8 w-8 mx-auto" />
                                                </div>
                                                <p className="text-sm text-red-700 font-medium mb-1">
                                                    No replacement status available
                                                </p>
                                                <p className="text-xs text-red-600">
                                                    You need at least one other active status to
                                                    delete this one. Please create a new status
                                                    first.
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </AlertDialogBody>
                        )}

                    <AlertDialogFooter className="gap-2">
                        <AlertDialogCancel
                            onClick={() => {
                                setDeletingStatus(null);
                                setReplacementStatusId('');
                            }}
                        >
                            Cancel
                        </AlertDialogCancel>
                        <AlertDialogAction
                            onClick={() => deletingStatus && handleDeleteStatus(deletingStatus)}
                            disabled={
                                deleteStatusMutation.isPending ||
                                (deletingStatus?.usage_count !== undefined &&
                                    deletingStatus.usage_count > 0 &&
                                    !replacementStatusId) ||
                                (deletingStatus?.usage_count !== undefined &&
                                    deletingStatus.usage_count > 0 &&
                                    statuses.filter(s => s.id !== deletingStatus?.id && s.is_active)
                                        .length === 0)
                            }
                            variant="destructive"
                            className="min-w-[120px]"
                        >
                            {deleteStatusMutation.isPending ? (
                                <>
                                    <div className="h-4 w-4 mr-2 animate-spin rounded-full border-2 border-current border-t-transparent" />
                                    Deleting...
                                </>
                            ) : (
                                <>
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    {deletingStatus?.usage_count &&
                                    deletingStatus.usage_count > 0 &&
                                    !replacementStatusId
                                        ? 'Select Replacement'
                                        : 'Delete Status'}
                                </>
                            )}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
};

// Sortable Status Item Component
interface SortableStatusItemProps {
    status: LeadStatus;
    index: number;
    onEdit: () => void;
    onToggle: () => void;
    onDelete: () => void;
    setReplacementStatusId: React.Dispatch<React.SetStateAction<string>>;
}

const SortableStatusItem: React.FC<SortableStatusItemProps> = ({
    status,
    onEdit,
    onToggle,
    onDelete,
    setReplacementStatusId,
}) => {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: status.id,
    });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className="flex items-center justify-between p-4 border rounded-lg bg-card"
        >
            <div className="flex items-center gap-4">
                <div className="flex items-center gap-2">
                    <div {...attributes} {...listeners} className="cursor-move">
                        <GripVertical className="h-4 w-4 text-muted-foreground" />
                    </div>
                    <Badge variant="outline" className="text-xs">
                        {status.sort_order}
                    </Badge>
                </div>

                <div className="flex items-center gap-3">
                    <div
                        className="w-4 h-4 rounded-full border-2 border-border"
                        style={{ backgroundColor: status.color }}
                    />
                    <div>
                        <div className="font-medium">{status.label}</div>
                        <div className="text-sm text-muted-foreground">
                            {status.name}
                            {status.usage_count !== undefined && status.usage_count > 0 && (
                                <Badge variant="outline" className="ml-2">
                                    {status.usage_count} in use
                                </Badge>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <div className="flex items-center gap-2">
                <Switch checked={status.is_active} onCheckedChange={onToggle} />

                <Button variant="ghost" size="sm" onClick={onEdit}>
                    <Edit className="h-4 w-4" />
                </Button>

                <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                        setReplacementStatusId('');
                        onDelete();
                    }}
                >
                    <Trash2 className="h-4 w-4 text-destructive" />
                </Button>
            </div>
        </div>
    );
};
